import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AshdaleLogoSVG from '../../assets/ashdale-logo.svg';

const { FiMail, FiLock, FiEye, FiEyeOff, FiShield } = FiIcons;

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const adminMode = location.state?.adminMode || false;
  const redirectTo = location.state?.redirectTo || '/portfolio';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      navigate(redirectTo);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        <div className="text-center">
          <img src={AshdaleLogoSVG} alt="Ashdale Analytics" className="h-12 mx-auto mb-6" />
          
          {adminMode ? (
            <div className="mb-6">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/30 text-primary-500 mb-4">
                <SafeIcon icon={FiShield} className="w-4 h-4 mr-2" />
                <span className="text-xs font-bold uppercase tracking-widest">Admin Verification Required</span>
              </div>
              <h2 className="text-3xl font-bold text-white">Identity Confirmed</h2>
              <p className="text-secondary-400 mt-2">Please sign in to access the Control Room</p>
            </div>
          ) : (
            <h2 className="text-3xl font-bold text-white">Sign in</h2>
          )}
        </div>

        <div className="mt-8 bg-secondary-900 py-8 px-4 shadow-xl sm:rounded-xl sm:px-10 border border-primary-500/10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-900/20 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-secondary-300 mb-1">Email</label>
              <div className="relative">
                <input
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 pl-11 bg-black border border-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="name@company.com"
                />
                <SafeIcon icon={FiMail} className="absolute left-4 top-3.5 h-5 w-5 text-secondary-500" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-300 mb-1">Password</label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 pl-11 bg-black border border-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                />
                <SafeIcon icon={FiLock} className="absolute left-4 top-3.5 h-5 w-5 text-secondary-500" />
                <button
                  type="button"
                  className="absolute right-4 top-3.5 text-secondary-500 hover:text-white"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-5 w-5" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={localLoading || authLoading}
              className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-400 text-black font-bold rounded-lg transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {localLoading ? 'Verifying...' : adminMode ? 'Enter Control Room' : 'Sign In'}
            </button>

            <div className="text-center mt-4">
              <Link 
                to="/forgot-password" 
                className="text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
              >
                Forgot Password?
              </Link>
            </div>
          </form>

          {!adminMode && (
            <p className="mt-8 text-center text-sm text-secondary-400 border-t border-secondary-800 pt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-500 hover:text-primary-400 font-semibold">
                Create one
              </Link>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Login;